<template>
  <div>
    <div class="d-flex justify-content-center align-items-center h-100 mt-4">
      <div class="card" style="width: min(450px, 100%)">
        <div class="card-body text-center">
          <div class="d-flex justify-content-center mb-4">
            <img src="@/assets/img/vactar.png" width="100"/>
          </div>
          <div v-if="status === 'home'">
            <HttpFormView endpoint="authentication/code/send" method="post" @success="status = $event.exists ? 'login' : 'register'">
              <template v-slot:form="{ content, errors, submit, loading }">
                <h2 class="mb-5 mt-3">{{ translate("auth.email.continue") }}</h2>
                <div class="d-flex flex-column g-4">
                  <InputView type="email" :title="translate('email')" :errors="errors.email" :value="content.email" @update:value="content.email = $event; email = content.email" :disabled="loading" required/>
                  <div class="text-muted text-center" style="font-size: 0.9em;">
                    {{ translate("auth.email.code.consent.text") }}
                  </div>
<!--                  <TurnstileView sitekey="0x4AAAAAAAB4knReukXdscSW" v-model:reset="resetTurnstile" @success="content.captcha = $event"/>-->
<!--                  <InputErrorsView :errors="errors.captcha"/>-->
                  <ButtonView type="normal" color="primary" :disabled="!content.email || loading" class="w-100" @click="submit()">
                    {{ translate("continue") }}
                  </ButtonView>
                  <SpinnerView :active="loading" color="primary"/>
                </div>
              </template>
            </HttpFormView>
          </div>
          <div v-else-if="status === 'register'">
            <HttpFormView endpoint="authentication/register" method="post" :content="{ email: email }" @success="info.reload()">
              <template v-slot:form="{ content, errors, submit, loading }">
                <h2 class="mb-5 mt-3">{{ translate("auth.account.create") }}</h2>
                <div class="d-flex flex-column g-4">
                  <InputView type="text" :title="translate('name')" :errors="errors.name" v-model:value="content.name" :max="24" required/>

                  <InputView type="text" :title="translate('auth.code')" :errors="errors.code" v-model:value="content.code" :max="6" required/>

                  <div style="font-size: 0.9em">
                    {{ translate("auth.code.description") }}
                  </div>

                  <CheckboxInputView class="text-start" :title="translate('auth.consent.text')" :errors="errors.consent" v-model:value="content.consent" color="primary"/>

                  <CheckboxInputView class="text-start" :title="translate('newsletter.consent.text')" :errors="errors.newsletterConsent" v-model:value="content.newsletterConsent" color="primary"/>

                  <ButtonView type="normal" color="primary" :disabled="!content.name || !content.code" class="w-100" @click="submit">
                    {{ translate("auth.account.create") }}
                  </ButtonView>

                  <ButtonView type="normal" color="secondary" class="w-100" @click="status = 'home'">
                    {{ translate("cancel") }}
                  </ButtonView>
                </div>

                <SpinnerView :active="loading || info.loading" color="primary"/>
              </template>
            </HttpFormView>
          </div>
          <div v-else-if="status === 'login'">
            <HttpFormView endpoint="authentication/login" method="post" :content="{ email: email }" @success="info.reload()">
              <template v-slot:form="{ content, errors, submit, loading }">
                <h2 class="mb-5 mt-3">{{ translate("auth.login") }}</h2>
                <div class="d-flex flex-column">
                  <InputView type="text" :title="translate('auth.code')" :errors="errors.code" v-model:value="content.code" :max="6" required/>

                  <div class="mt-3 mb-5" style="font-size: 0.9em">
                    {{ translate("auth.code.description") }}
                  </div>

                  <ButtonView type="normal" color="primary" :disabled="!content.code" class="w-100 mb-3" @click="submit">
                    {{ translate("auth.login") }}
                  </ButtonView>

                  <ButtonView type="normal" color="secondary" class="w-100" @click="status = 'home'">
                    {{ translate("cancel") }}
                  </ButtonView>
                </div>

                <SpinnerView :active="loading || info.loading" color="primary"/>
              </template>
            </HttpFormView>
          </div>
          <div class="d-flex justify-content-center g-1 align-items-center mt-4" style="word-break: break-all">
            <ButtonView type="shaded" color="primary" size="small" @click="openTerms">
              {{ translate("terms.of.service") }}
            </ButtonView>
            <ButtonView type="shaded" color="primary" size="small" @click="openPrivacy">
              {{ translate("privacy.policy") }}
            </ButtonView>
            <ButtonView v-if="info.global?.languages" type="shaded" color="foreground" @click="changingLanguage = true">
              {{ info.global.languages.find(item => item.code === (info.global.language ?? localization.browserLanguage()))?.flag }}
            </ButtonView>
          </div>
        </div>
      </div>
    </div>
    <ModalView v-model:active="changingLanguage" size="small">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between g-4">
          <h3 class="mb-0">{{ translate("language") }}</h3>
          <ModalCloseButtonView @click="changingLanguage = false"/>
        </div>
      </template>
      <template v-slot:body>
        <HttpFormView endpoint="session/language" method="post" :content="{ code: info.global.language ?? info.global.languages.find(item => item.code === localization.browserLanguage())?.code ?? 'en' }" @success="info.reload(); localization.reloadTranslations(); changingLanguage = false">
          <template v-slot:form="{ content, submit, loading }">
            <div class="list-group">
              <button v-for="language in info.global.languages" :key="language.code" class="list-group-item list-group-item-action" @click="content.code = language.code; submit()" :disabled="loading">
                <i v-if="language.code === (info.global.language ?? localization.browserLanguage())" class="fas fa-check-circle text-primary"></i>
                <i v-else class="far fa-circle text-primary"></i>
                {{ language.flag }} {{ language.name }}
              </button>
            </div>
            <SpinnerView color="primary" :active="loading"/>
          </template>
        </HttpFormView>
      </template>
    </ModalView>
  </div>
</template>

<script setup>
import {InputView, HttpFormView, ButtonView, SpinnerView, CheckboxInputView, ModalView, ModalCloseButtonView} from "@niftybit/vue";
import {ref} from "vue";
import info from "@/info";
import localization from "@/localization";

const status = ref("home");

const email = ref(undefined);

const changingLanguage = ref(false);

// const resetTurnstile = ref(false);

function openTerms() {
  window.open('https://vactar.io/policies/terms', '_blank')
}

function openPrivacy() {
  window.open('https://vactar.io/policies/privacy', '_blank');
}

</script>